import { Injectable, signal, computed } from '@angular/core';
import { Observable, Subscriber } from 'rxjs';

/**
 * LoadingService
 *
 * Manages global and scoped loading states throughout the application.
 * Uses Angular Signals for reactive state management.
 *
 * @example
 * ```typescript
 * // In component
 * private loading = inject(LoadingService);
 *
 * // Check global loading state
 * isLoading = this.loading.isLoading;
 *
 * // In template
 * @if (loading.isLoading()) {
 *   <app-loading-overlay />
 * }
 *
 * // Start/stop loading with key
 * this.loading.start('vendors');
 * // ... after operation
 * this.loading.stop('vendors');
 *
 * // Check specific loading state
 * this.loading.isLoadingKey('vendors');
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class LoadingService {
  /**
   * Map of loading keys to their active state
   * Using a Map allows multiple concurrent loading operations
   */
  private loadingKeys = signal<Map<string, boolean>>(new Map());

  /**
   * Global loading state - true if any key is loading
   */
  readonly isLoading = computed(() => {
    const keys = this.loadingKeys();
    return Array.from(keys.values()).some(value => value);
  });

  /**
   * Number of active loading operations
   */
  readonly loadingCount = computed(() => {
    const keys = this.loadingKeys();
    return Array.from(keys.values()).filter(value => value).length;
  });

  /**
   * Start loading for a specific key
   * @param key - Unique identifier for this loading operation
   */
  start(key: string = 'global'): void {
    this.loadingKeys.update(keys => {
      const newKeys = new Map(keys);
      newKeys.set(key, true);
      return newKeys;
    });
  }

  /**
   * Stop loading for a specific key
   * @param key - Unique identifier for this loading operation
   */
  stop(key: string = 'global'): void {
    this.loadingKeys.update(keys => {
      const newKeys = new Map(keys);
      newKeys.delete(key);
      return newKeys;
    });
  }

  /**
   * Check if a specific key is loading
   * @param key - The key to check
   */
  isLoadingKey(key: string): boolean {
    return this.loadingKeys().get(key) ?? false;
  }

  /**
   * Get a computed signal for a specific key's loading state
   * Useful for binding in templates
   * @param key - The key to track
   */
  loadingSignal(key: string) {
    return computed(() => this.loadingKeys().get(key) ?? false);
  }

  /**
   * Clear all loading states
   * Useful for cleanup or error recovery
   */
  clearAll(): void {
    this.loadingKeys.set(new Map());
  }

  /**
   * Wrap an async operation with loading state
   * Automatically starts and stops loading
   *
   * @example
   * ```typescript
   * const result = await this.loading.wrap('fetchVendors', async () => {
   *   return this.vendorService.getAll();
   * });
   * ```
   */
  async wrap<T>(key: string, operation: () => Promise<T>): Promise<T> {
    this.start(key);
    try {
      return await operation();
    } finally {
      this.stop(key);
    }
  }

  /**
   * Create an RxJS operator that manages loading state
   * Usage: .pipe(this.loading.withLoading('myKey'))
   */
  withLoading<T>(key: string) {
    return (source: Observable<T>): Observable<T> => {
      return new Observable((subscriber: Subscriber<T>) => {
        this.start(key);

        const subscription = source.subscribe({
          next: (value) => subscriber.next(value),
          error: (err) => {
            this.stop(key);
            subscriber.error(err);
          },
          complete: () => {
            this.stop(key);
            subscriber.complete();
          },
        });

        return () => {
          subscription.unsubscribe();
          this.stop(key);
        };
      });
    };
  }
}
